import { useState, useEffect, useContext } from "react";
import { Modal, Form, Button, Alert, Spinner, Row, Col, Badge } from "react-bootstrap";
import { IconCreditCard, IconCheck, IconExclamationCircleFilled, IconUsers, IconRobot } from "@tabler/icons-react";
import Select from "react-select";
import Swal from "sweetalert2";
import UserContext from "../../../contexts/UserContext";
import SubaccountService from "../../../services/SubaccountServices";
import {
  listBillingProfilesForPayment,
  createSubscriptionWithProfile,
  validateSubscriptionData,
  formatCurrency,
  getCurrentDate,
  formatBillingProfileForSelect,
  processSubscriptionResponse,
  processSubscriptionError,
} from "../../../services/CustomerPaymentServices";

// Configuração dos tipos de contratação
const CONTRACT_TYPES = {
  subaccount: {
    label: "Subconta Adicional",
    unitLabel: "subaccount",
    unitLabelPlural: "subaccounts",
    unitValue: 150,
    icon: <IconUsers size={18} />,
    description: "Assinatura subconta de IA - MercadoAgentes",
    quantityLabel: "Quantidade de Subcontas Adicionais",
    quantityHint: (price) => `Valor unitário: ${price} por subconta/mês`,
    totalHint: (qty, price) => `${qty} subconta(s) × ${price}`,
    infoTitle: "Sobre a assinatura de subconta",
    infoText:
      "Cada assinatura de subconta permitirá você adicionar uma nova subconta sob demanda em seu plano. Você poderá cancelar a assinatura a qualquer momento, porém todos os dados da subconta serão excluídos.",
    successLabel: "Quantidade de Subcontas",
  },
  agent: {
    label: "Agente Adicional",
    unitLabel: "agent",
    unitLabelPlural: "agentes",
    unitValue: 150,
    icon: <IconRobot size={18} />,
    description: "Assinatura agente adicional de IA - MercadoAgentes",
    quantityLabel: "Quantidade de Agentes Adicionais",
    quantityHint: (price) => `Valor unitário: ${price} por agente/mês`,
    totalHint: (qty, price) => `${qty} agente(s) × ${price}`,
    infoTitle: "Sobre a assinatura de agente",
    infoText:
      "Cada assinatura de agente adicional permitirá publicar um novo agente de IA em sua subconta selecionada. Você poderá cancelar a assinatura a qualquer momento.",
    successLabel: "Quantidade de Agentes",
  },
};

const contractTypeOptions = [
  { value: "subaccount", label: "Subconta Adicional", icon: "👥" },
  { value: "agent", label: "Agente Adicional", icon: "🤖" },
];

const SubscriptionPaymentModal = ({
  show,
  onHide,
  paymentData, // { value, description, agentName, clientName }
  onSuccess
}) => {
  const { user, isOrganization } = useContext(UserContext);

  // Tipo de contratação
  const [contractType, setContractType] = useState("subaccount");

  // Estados do formulário
  const [formData, setFormData] = useState({
    billing_profile_id: null,
    value: 150,
    description: CONTRACT_TYPES.subaccount.description,
    next_due_date: getCurrentDate(),
    cycle: "MONTHLY",
    additional_agents: 1,
  });

  // Estados de controle
  const [loading, setLoading] = useState(false);
  const [profilesLoading, setProfilesLoading] = useState(false);
  const [billingProfiles, setBillingProfiles] = useState([]);
  const [subaccounts, setSubaccounts] = useState([]);
  const [subaccountsLoading, setSubaccountsLoading] = useState(false);
  const [selectedSubaccount, setSelectedSubaccount] = useState(null);
  const [errors, setErrors] = useState({});

  const config = CONTRACT_TYPES[contractType];
  const unitValue = config.unitValue;

  useEffect(() => {
    if (show) {
      loadBillingProfiles();
      loadSubaccounts();
      setErrors({});

      if (paymentData) {
        setFormData(prev => ({
          ...prev,
          description: paymentData.description || prev.description,
        }));
      }
    }
  }, [show, paymentData]);

  // Recalcular valor quando tipo ou quantidade mudar
  useEffect(() => {
    setFormData(prev => ({
      ...prev,
      value: unitValue * prev.additional_agents,
      description: config.description,
    }));
  }, [contractType, formData.additional_agents, unitValue, config.description]);

  const loadBillingProfiles = async () => {
    try {
      setProfilesLoading(true);
      const response = await listBillingProfilesForPayment();

      if (response.data && response.data.results) {
        const profileOptions = response.data.results.map(formatBillingProfileForSelect);
        setBillingProfiles(profileOptions);

        const defaultProfile = profileOptions.find(option => option.profile.is_default);
        if (defaultProfile) {
          setFormData(prev => ({ ...prev, billing_profile_id: defaultProfile }));
        }
      } else {
        setBillingProfiles([]);
      }
    } catch (error) {
      console.error("Erro ao carregar perfis de faturamento:", error);
      setBillingProfiles([]);

      Swal.fire({
        icon: "error",
        title: "Erro",
        text: "Não foi possível carregar os perfis de faturamento.",
        confirmButtonColor: "#ef4444",
      });
    } finally {
      setProfilesLoading(false);
    }
  };

  const loadSubaccounts = async () => {
    try {
      setSubaccountsLoading(true);
      const data = await SubaccountService.getSubaccounts();
      const results = data.results || data || [];
      const options = results
        .filter((s) => s.subaccount_active)
        .map((s) => ({
          value: s.id,
          label: s.name || s.company_name || `Subconta #${s.id}`,
          subaccount: s,
        }));
      setSubaccounts(options);
    } catch (error) {
      console.error("Erro ao carregar subcontas:", error);
      setSubaccounts([]);
    } finally {
      setSubaccountsLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));

    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: "" }));
    }
  };

  const handleContractTypeChange = (type) => {
    const newConfig = CONTRACT_TYPES[type];
    setContractType(type);
    setSelectedSubaccount(null);
    setFormData(prev => ({
      ...prev,
      additional_agents: 1,
      value: newConfig.unitValue,
      description: newConfig.description,
    }));
    if (errors.subaccount_id) {
      setErrors(prev => ({ ...prev, subaccount_id: "" }));
    }
  };

  const validateForm = () => {
    const payload = {
      ...formData,
      billing_profile_id: formData.billing_profile_id?.value,
    };

    const validation = validateSubscriptionData(payload);
    const newErrors = { ...validation.errors };

    // Validar subconta quando tipo é agente
    if (contractType === "agent" && !selectedSubaccount) {
      newErrors.subaccount_id = "Selecione a subconta para o agente";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);

      const now = new Date().toISOString().split("T")[0];

      const payload = {
        billing_profile_id: formData.billing_profile_id.value,
        value: formData.value,
        description: formData.description,
        next_due_date: now,
        cycle: formData.cycle,
        internal: 1,
        contract_type: contractType,
        ...(contractType === "agent" && selectedSubaccount && {
          subaccount_id: selectedSubaccount.value,
        }),
      };

      const response = await createSubscriptionWithProfile(payload);

      if (response.status === 201 && response.data) {
        const subscriptionResult = processSubscriptionResponse(response);

        const currentConfig = CONTRACT_TYPES[contractType];
        Swal.fire({
          icon: "success",
          title: "Pagamento Processado!",
          html: `
            <div style="text-align: left; padding: 15px;">
              <div class="alert alert-success" style="margin: 10px 0;">
                <h6><strong>Assinatura Criada com Sucesso</strong></h6>
                <p><strong>Tipo:</strong> ${currentConfig.label}</p>
                ${selectedSubaccount ? `<p><strong>Subconta:</strong> ${selectedSubaccount.label}</p>` : ''}
                <p><strong>ID:</strong> ${subscriptionResult.subscriptionId}</p>
                <p><strong>${currentConfig.successLabel}:</strong> ${formData.additional_agents}</p>
                <p><strong>Valor:</strong> ${subscriptionResult.formattedValue}</p>
                <p><strong>Próximo vencimento:</strong> ${subscriptionResult.formattedNextDueDate}</p>
              </div>

              <hr>
              <p class="text-muted">${subscriptionResult.message}</p>
            </div>
          `,
          confirmButtonText: "Continuar",
          confirmButtonColor: "#06b776",
        });

        if (onSuccess) onSuccess(subscriptionResult);
        onHide();
        return;
      } else {
        console.log("Resposta inesperada da API:", response);
      }
    } catch (error) {
      console.error("Erro ao processar pagamento:", error);

      const errorInfo = processSubscriptionError(error);

      Swal.fire({
        icon: "error",
        title: "Erro no Pagamento",
        html: `
          <div style="text-align: left; padding: 15px;">
            <div class="alert alert-danger" style="margin: 10px 0;">
              <h6><strong>Falha no Processamento</strong></h6>
              <p>${errorInfo.message}</p>
            </div>

            ${errorInfo.status ? `<p><strong>Código:</strong> ${errorInfo.status}</p>` : ''}

            <hr>
            <p class="text-muted">Verifique os dados do cartão e tente novamente, ou entre em contato com o suporte.</p>
          </div>
        `,
        confirmButtonColor: "#ef4444",
      });
    } finally {
      setLoading(false);
    }
  };

  const customSelectStyles = {
    control: (provided) => ({
      ...provided,
      borderColor: errors.billing_profile_id ? "#ef4444" : provided.borderColor,
      "&:hover": {
        borderColor: errors.billing_profile_id ? "#ef4444" : provided.borderColor,
      },
      minHeight: "38px",
    }),
    menu: (provided) => ({
      ...provided,
      zIndex: 9999,
    }),
    menuPortal: (provided) => ({
      ...provided,
      zIndex: 9999,
    }),
  };

  const isFormValid =
    formData.billing_profile_id &&
    formData.value > 0 &&
    formData.description &&
    formData.additional_agents > 0 &&
    (contractType !== "agent" || selectedSubaccount);

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title className="d-flex align-items-center">
          <IconCreditCard size={24} className="me-2" />
          Pagamento de Assinatura
        </Modal.Title>
      </Modal.Header>

      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          {/* Seletor de Tipo de Contratação */}
          <div className="mb-4">
            <Form.Label>
              <strong>O que deseja contratar?</strong>
            </Form.Label>
            <div className="d-flex gap-2">
              {contractTypeOptions.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  className={`flex-fill d-flex align-items-center justify-content-center gap-2 p-3 rounded-3 border ${
                    contractType === opt.value
                      ? "border-primary bg-primary bg-opacity-10 text-primary fw-semibold"
                      : "border-light-subtle bg-white text-secondary"
                  }`}
                  style={{
                    cursor: "pointer",
                    transition: "all 0.15s ease",
                    borderWidth: contractType === opt.value ? "2px" : "1px",
                  }}
                  onClick={() => handleContractTypeChange(opt.value)}
                >
                  <span style={{ fontSize: "20px" }}>{opt.icon}</span>
                  <span>{opt.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Informações da Assinatura */}
          <Alert variant="info" className="mb-4">
            <div className="d-flex align-items-center mb-2">
              <IconExclamationCircleFilled size={20} className="me-2" />
              <strong>Detalhes da Assinatura — {config.label}</strong>
            </div>
            <Row className="g-2">
              <Col sm={6}>
                <strong>Quantidade:</strong>{" "}
                {formData.additional_agents} {formData.additional_agents === 1 ? config.unitLabel : config.unitLabelPlural}
              </Col>
              <Col sm={6}>
                <strong>Valor Unitário:</strong> {formatCurrency(unitValue)}
              </Col>
              <Col sm={6}>
                <strong>Valor Total:</strong> {formatCurrency(formData.value)}
              </Col>
              <Col sm={6}>
                <strong>Ciclo:</strong> Mensal
              </Col>
            </Row>
          </Alert>

          {/* Verificação de permissões */}
          {!isOrganization() ? (
            <Alert variant="warning">
              Esta funcionalidade está disponível apenas para organizações.
            </Alert>
          ) : user?.superuser_level > 2 ? (
            <Alert variant="warning">
              Você não tem permissão para realizar pagamentos.
            </Alert>
          ) : (
            <Row>
              {/* Quantidade */}
              <Col md={6} className="mb-3">
                <Form.Label>
                  <strong>{config.quantityLabel} *</strong>
                </Form.Label>
                <Form.Control
                  type="number"
                  min="1"
                  max="100"
                  value={formData.additional_agents}
                  onChange={(e) =>
                    handleInputChange("additional_agents", Math.max(1, parseInt(e.target.value) || 1))
                  }
                  placeholder={`Quantidade de ${config.unitLabelPlural}`}
                  disabled={loading}
                  isInvalid={!!errors.additional_agents}
                />
                <Form.Text className="text-muted">
                  {config.quantityHint(formatCurrency(unitValue))}
                </Form.Text>
                {errors.additional_agents && (
                  <Form.Control.Feedback type="invalid">
                    {errors.additional_agents}
                  </Form.Control.Feedback>
                )}
              </Col>

              {/* Subconta destino (apenas para agente) */}
              {contractType === "agent" && (
                <Col xs={12} className="mb-3">
                  <Form.Label>
                    <strong>Subconta de destino *</strong>
                  </Form.Label>
                  {subaccountsLoading ? (
                    <div className="d-flex align-items-center p-3 border rounded">
                      <Spinner size="sm" className="me-2" />
                      <span>Carregando subcontas...</span>
                    </div>
                  ) : subaccounts.length === 0 ? (
                    <Alert variant="warning" className="mb-0">
                      <strong>Nenhuma subconta ativa encontrada.</strong> Você precisa
                      ter pelo menos uma subconta ativa para contratar agentes adicionais.
                    </Alert>
                  ) : (
                    <>
                      <Select
                        value={selectedSubaccount}
                        onChange={(option) => {
                          setSelectedSubaccount(option);
                          if (errors.subaccount_id) {
                            setErrors(prev => ({ ...prev, subaccount_id: "" }));
                          }
                        }}
                        options={subaccounts}
                        placeholder="Selecione a subconta..."
                        noOptionsMessage={() => "Nenhuma subconta disponível"}
                        isSearchable
                        styles={{
                          ...customSelectStyles,
                          control: (provided) => ({
                            ...provided,
                            borderColor: errors.subaccount_id ? "#ef4444" : provided.borderColor,
                            "&:hover": {
                              borderColor: errors.subaccount_id ? "#ef4444" : provided.borderColor,
                            },
                            minHeight: "38px",
                          }),
                        }}
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                      />
                      {selectedSubaccount && (
                        <div className="mt-2 p-2 bg-light rounded">
                          <small className="text-muted">
                            O agente adicional será vinculado à subconta{" "}
                            <strong>{selectedSubaccount.label}</strong>
                          </small>
                        </div>
                      )}
                    </>
                  )}
                  {errors.subaccount_id && (
                    <div className="invalid-feedback d-block">
                      {errors.subaccount_id}
                    </div>
                  )}
                </Col>
              )}

              {/* Valor Total (readonly) */}
              <Col md={6} className="mb-3">
                <Form.Label>
                  <strong>Valor Total Mensal *</strong>
                </Form.Label>
                <Form.Control
                  type="text"
                  value={formatCurrency(formData.value)}
                  disabled
                  readOnly
                />
                <Form.Text className="text-muted">
                  {config.totalHint(formData.additional_agents, formatCurrency(unitValue))}
                </Form.Text>
              </Col>

              {/* Seleção do Perfil de Faturamento */}
              <Col xs={12} className="mb-4">
                <Form.Label>
                  <strong>Perfil de Faturamento *</strong>
                </Form.Label>
                {profilesLoading ? (
                  <div className="d-flex align-items-center p-3 border rounded">
                    <Spinner size="sm" className="me-2" />
                    <span>Carregando perfis de faturamento...</span>
                  </div>
                ) : billingProfiles.length === 0 ? (
                  <Alert variant="warning">
                    <strong>Nenhum perfil encontrado.</strong> Você precisa
                    criar um perfil de faturamento antes de realizar pagamentos.
                  </Alert>
                ) : (
                  <>
                    <Select
                      value={formData.billing_profile_id}
                      onChange={(selectedOption) =>
                        handleInputChange("billing_profile_id", selectedOption)
                      }
                      options={billingProfiles}
                      placeholder="Selecione um perfil de faturamento..."
                      noOptionsMessage={() => "Nenhum perfil disponível"}
                      isSearchable
                      styles={customSelectStyles}
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                    />
                    {formData.billing_profile_id && (
                      <div className="mt-2 p-2 bg-light rounded">
                        <small className="text-muted">
                          <strong>Cartão selecionado:</strong>{" "}
                          {formData.billing_profile_id.profile.card_brand} ****{" "}
                          {formData.billing_profile_id.profile.card_last_digits}
                          {formData.billing_profile_id.profile.is_default && (
                            <Badge bg="primary" className="ms-2">
                              Padrão
                            </Badge>
                          )}
                        </small>
                      </div>
                    )}
                  </>
                )}
                {errors.billing_profile_id && (
                  <div className="invalid-feedback d-block">
                    {errors.billing_profile_id}
                  </div>
                )}
              </Col>

              {/* Descrição */}
              <Col xs={12} className="mb-3">
                <Form.Label>
                  <strong>Descrição *</strong>
                </Form.Label>
                <Form.Control
                  type="text"
                  value={formData.description}
                  onChange={(e) =>
                    handleInputChange("description", e.target.value)
                  }
                  placeholder="Descrição da assinatura..."
                  disabled={loading}
                  isInvalid={!!errors.description}
                />
                {errors.description && (
                  <Form.Control.Feedback type="invalid">
                    {errors.description}
                  </Form.Control.Feedback>
                )}
              </Col>

              {/* Data de início da cobrança */}
              <Col xs={12} className="mb-3">
                <Form.Label>
                  <strong>Início da Cobrança *</strong>
                </Form.Label>
                <Form.Control
                  type="text"
                  value={formData.next_due_date}
                  disabled
                  readOnly
                />
                <Form.Text className="text-muted">
                  A cobrança inicia hoje
                </Form.Text>
                <div className="mt-2 p-2 rounded" style={{ backgroundColor: "#eff6ff", border: "1px solid #dbeafe" }}>
                  <small style={{ color: "#1e40af", lineHeight: "1.5" }}>
                    Esta é uma assinatura avulsa e independente do seu plano principal.
                    A cobrança começa hoje pois possui ciclo próprio e pode ser cancelada a qualquer momento.
                  </small>
                </div>
              </Col>
            </Row>
          )}

          {/* Avisos */}
          {isOrganization() && user?.superuser_level <= 2 && (
            <>
              <Alert variant="info" className="mt-3">
                <div className="d-flex align-items-center">
                  <IconCheck size={16} className="me-2" />
                  <div>
                    <strong>{config.infoTitle}</strong>
                    <p className="mb-0 mt-1">
                      {config.infoText}
                    </p>
                  </div>
                </div>
              </Alert>
              <Alert variant="success" className="mt-3">
                <div className="d-flex align-items-center">
                  <IconCheck size={16} className="me-2" />
                  <div>
                    <strong>Transação Segura</strong>
                    <p className="mb-0 mt-1">
                      Seus dados estão protegidos com criptografia SSL e
                      tokenização de cartão.
                    </p>
                  </div>
                </div>
              </Alert>
            </>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={onHide} disabled={loading}>
            Cancelar
          </Button>
          {isOrganization() &&
            user?.superuser_level <= 2 &&
            billingProfiles.length > 0 && (
              <Button
                variant="success"
                type="submit"
                disabled={loading || !isFormValid}
              >
                {loading ? (
                  <>
                    <Spinner size="sm" className="me-2" />
                    Processando...
                  </>
                ) : (
                  <>
                    <IconCreditCard size={16} className="me-2" />
                    Confirmar Pagamento — {formatCurrency(formData.value)}
                  </>
                )}
              </Button>
            )}
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default SubscriptionPaymentModal;
